import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User as UserIcon, LogIn, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Block direct URL access to /admin — require flag set by hidden footer link or Ctrl+X
    if (sessionStorage.getItem('admin_access') !== 'granted') {
      navigate('/', { replace: true });
      return;
    }
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios
        .get(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => navigate('/admin/dashboard'))
        .catch(() => localStorage.removeItem('admin_token'));
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, { username, password });
      localStorage.setItem('admin_token', data.access_token);
      toast.success('Anmeldung erfolgreich');
      navigate('/admin/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      toast.error(typeof detail === 'string' ? detail : 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="absolute top-4 left-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-slate-600 hover:text-[#40E0D0]"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Startseite
        </Button>
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-10 border border-slate-100">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 bg-[#E0F7FA] rounded-2xl flex items-center justify-center mb-4">
              <Lock className="w-7 h-7 text-[#40E0D0]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 font-serif" style={{ fontFamily: 'Merriweather, serif' }}>
              Admin-Bereich
            </h1>
            <p className="text-sm text-slate-500 mt-1">Großstadtherzen Pflegeberatung</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="username">Benutzername</Label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  className="pl-10"
                  data-testid="admin-username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pl-10"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#40E0D0] hover:bg-[#36c4b6] text-slate-900 font-semibold"
              data-testid="admin-login-submit"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loading ? 'Anmelden…' : 'Anmelden'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
